import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { calcularPrecios, DEFAULTS } from '../lib/precios';
import './AdminTasa.css'; // reutiliza estilos del panel de tasa

const SANITY_PROJECT = 'wwy5bykm';
const SANITY_DATASET = 'production';
const SANITY_API_V = '2024-01-01';

// Productos de prueba para el preview (3 puntos representativos)
const MUESTRAS_PREVIEW = [
  { nombre: 'Producto económico',  costoUSD: 60  },
  { nombre: 'Producto medio',      costoUSD: 120 },
  { nombre: 'Producto alto',       costoUSD: 400 },
];

export default function AdminPrecios() {
  const [actual, setActual]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState(null);
  const [password, setPassword] = useState('');

  // Form state
  const [form, setForm] = useState({
    tasaInterna:      '',
    margenCasheaBajo: '',
    margenCasheaAlto: '',
    umbralCashea:     '',
    margenKrece:      '',
  });

  // Cargar config actual
  useEffect(() => {
    const query = encodeURIComponent('*[_id=="configuracion-principal"][0]{tasaCambio,tasaInterna,margenCasheaBajo,margenCasheaAlto,umbralCashea,margenKrece}');
    fetch(`https://${SANITY_PROJECT}.api.sanity.io/v${SANITY_API_V}/data/query/${SANITY_DATASET}?query=${query}`)
      .then(r => r.json())
      .then(d => {
        const c = d.result || {};
        setActual(c);
        setForm({
          tasaInterna:      c.tasaInterna ?? '',
          margenCasheaBajo: c.margenCasheaBajo ?? DEFAULTS.margenCasheaBajo,
          margenCasheaAlto: c.margenCasheaAlto ?? DEFAULTS.margenCasheaAlto,
          umbralCashea:     c.umbralCashea     ?? DEFAULTS.umbralCashea,
          margenKrece:      c.margenKrece      ?? DEFAULTS.margenKrece,
        });
      })
      .catch(err => console.error('No se pudo cargar config:', err))
      .finally(() => setLoading(false));
  }, []);

  // Preview en vivo combinando form + tasaCambio actual
  const previewConfig = useMemo(() => ({
    tasaCambio:       actual?.tasaCambio,
    tasaInterna:      parseFloat(form.tasaInterna)      || 0,
    margenCasheaBajo: parseFloat(form.margenCasheaBajo) || 0,
    margenCasheaAlto: parseFloat(form.margenCasheaAlto) || 0,
    umbralCashea:     parseFloat(form.umbralCashea)     || 0,
    margenKrece:      parseFloat(form.margenKrece)      || 0,
  }), [form, actual]);

  const factorAjuste = (previewConfig.tasaInterna && previewConfig.tasaCambio)
    ? (previewConfig.tasaInterna / previewConfig.tasaCambio)
    : null;

  const handleChange = (k) => (e) => {
    setForm(s => ({ ...s, [k]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);

    try {
      const res = await fetch('/api/admin/update-precios', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          tasaInterna:      previewConfig.tasaInterna,
          margenCasheaBajo: previewConfig.margenCasheaBajo,
          margenCasheaAlto: previewConfig.margenCasheaAlto,
          umbralCashea:     previewConfig.umbralCashea,
          margenKrece:      previewConfig.margenKrece,
        }),
      });
      const data = await res.json();

      if (data.success) {
        setMsg({ type: 'success', text: '✅ Configuración actualizada. Los precios se recalcularán automáticamente.' });
        setActual(prev => ({ ...prev, ...data.updated }));
        setPassword('');
      } else {
        setMsg({ type: 'error', text: `❌ ${data.error}` });
      }
    } catch (err) {
      setMsg({ type: 'error', text: '❌ Error de conexión' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-tasa"><div className="admin-tasa__card"><p>Cargando...</p></div></div>;
  }

  return (
    <div className="admin-tasa">
      <div className="admin-tasa__card" style={{ maxWidth: 720 }}>
        <div className="admin-tasa__logo">
          <img src="/logos/nuovocell-logo.png" alt="Nuovocell" />
        </div>

        <h1 className="admin-tasa__title">Configuración de precios</h1>

        <p style={{ textAlign: 'center', color: '#666', fontSize: 14, marginBottom: 20 }}>
          Tasa BCV actual: <strong>Bs. {actual?.tasaCambio?.toLocaleString('es-VE', { minimumFractionDigits: 2 }) || '—'}</strong>
          <br />
          <Link to="/admin/tasa" style={{ fontSize: 13 }}>Actualizar tasa BCV →</Link>
        </p>

        <form className="admin-tasa__form" onSubmit={handleSubmit}>
          <div className="admin-tasa__group">
            <label>Tasa interna (Bs. por $1)</label>
            <input
              type="number" step="0.01" min="0"
              placeholder="Ej: 750.00"
              value={form.tasaInterna}
              onChange={handleChange('tasaInterna')}
              required
            />
            {factorAjuste && (
              <small style={{ color: '#666', fontSize: 12 }}>
                Factor de ajuste cambiario: ×{factorAjuste.toFixed(4)}
              </small>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="admin-tasa__group">
              <label>Margen Cashea (costo &lt; umbral)</label>
              <input type="number" step="0.01" min="0" max="5"
                value={form.margenCasheaBajo}
                onChange={handleChange('margenCasheaBajo')}
                required />
              <small style={{ color: '#666', fontSize: 12 }}>
                {(form.margenCasheaBajo * 100).toFixed(1)}%
              </small>
            </div>
            <div className="admin-tasa__group">
              <label>Margen Cashea (costo ≥ umbral)</label>
              <input type="number" step="0.01" min="0" max="5"
                value={form.margenCasheaAlto}
                onChange={handleChange('margenCasheaAlto')}
                required />
              <small style={{ color: '#666', fontSize: 12 }}>
                {(form.margenCasheaAlto * 100).toFixed(1)}%
              </small>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="admin-tasa__group">
              <label>Umbral de costo (USD)</label>
              <input type="number" step="1" min="0"
                value={form.umbralCashea}
                onChange={handleChange('umbralCashea')}
                required />
            </div>
            <div className="admin-tasa__group">
              <label>Margen Krece / Weppa</label>
              <input type="number" step="0.01" min="0" max="5"
                value={form.margenKrece}
                onChange={handleChange('margenKrece')}
                required />
              <small style={{ color: '#666', fontSize: 12 }}>
                {(form.margenKrece * 100).toFixed(1)}%
              </small>
            </div>
          </div>

          {/* ============ PREVIEW EN VIVO ============ */}
          <div style={{
            background: '#f5f7fa', borderRadius: 8, padding: 16, marginTop: 16,
            border: '1px solid #e3e8ee'
          }}>
            <h3 style={{ marginTop: 0, fontSize: 15, marginBottom: 12 }}>
              Vista previa (con valores actuales del formulario)
            </h3>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #ccd' }}>
                  <th style={{ textAlign: 'left', padding: '6px 4px' }}>Producto</th>
                  <th style={{ textAlign: 'right', padding: '6px 4px' }}>Costo</th>
                  <th style={{ textAlign: 'right', padding: '6px 4px' }}>Cashea $</th>
                  <th style={{ textAlign: 'right', padding: '6px 4px' }}>Cashea Bs.</th>
                  <th style={{ textAlign: 'right', padding: '6px 4px' }}>Krece $</th>
                </tr>
              </thead>
              <tbody>
                {MUESTRAS_PREVIEW.map(p => {
                  const r = calcularPrecios(p, previewConfig);
                  return (
                    <tr key={p.nombre} style={{ borderBottom: '1px solid #eef' }}>
                      <td style={{ padding: '6px 4px' }}>{p.nombre}</td>
                      <td style={{ padding: '6px 4px', textAlign: 'right' }}>${p.costoUSD}</td>
                      <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                        <strong>{r ? `$${r.casheaUSD}` : '—'}</strong>
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                        {r ? `Bs. ${r.casheaBs.toLocaleString('es-VE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—'}
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'right', color: '#888' }}>
                        {r ? `$${r.kreceUSD}` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="admin-tasa__group" style={{ marginTop: 16 }}>
            <label>Contraseña</label>
            <input
              type="password"
              placeholder="Contraseña de administrador"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>

          {msg && (
            <div className={`admin-tasa__msg admin-tasa__msg--${msg.type}`}>
              {msg.text}
            </div>
          )}

          <button type="submit" className="admin-tasa__btn" disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar configuración'}
          </button>
        </form>

        <p className="admin-tasa__note">
          Al guardar, todos los productos con costo definido recalculan su precio automáticamente.
          Los productos con precio fijo manual no se ven afectados.
        </p>
      </div>
    </div>
  );
}
